import { Button, ButtonProps } from '@components/ui/button';
import { useFormContext } from '@components/ui/form/form';
import { useStore } from '@tanstack/react-form';
import { useSpinDelay } from 'spin-delay';

export type FormButtonProps = Omit<ButtonProps, 'children'> & {
  children?: React.ReactNode | ((props: { isSubmitting: boolean; canSubmit: boolean }) => React.ReactNode);
};

/**
 * A form-aware button component that automatically handles submission states.
 * Displays a loading state and disables interaction during form submission.
 * @param props - Standard button props with optional render prop children
 * @returns A form-integrated button that shows submission status
 */
export function FormButton(props: FormButtonProps) {
  const { children, ...rest } = props;
  const form = useFormContext();

  const { isSubmitting: formIsSubmitting, canSubmit: formCanSubmit } = useStore(form.store, (state) => ({
    isSubmitting: state.isSubmitting,
    canSubmit: state.canSubmit,
  }));

  const isSubmitting = useSpinDelay(formIsSubmitting, { delay: 0, minDuration: 200 });
  const canSubmit = useSpinDelay(formCanSubmit, { delay: 0, minDuration: 200 });

  return (
    <Button
      className="w-full"
      disabled={!canSubmit || isSubmitting}
      type="submit"
      {...rest}
    >
      {typeof children === 'function'
        ? children({ isSubmitting, canSubmit })
        : isSubmitting
          ? 'Submitting...'
          : children}
    </Button>
  );
}
